import {
  Component,
  OnInit,
  Inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Announcement } from '@models/announcement';
import { BehaviorSubject } from 'rxjs';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { RecurrenceService } from 'src/app/core/services/recurrence.service';
import { EventFormValues } from '../../../manage/activities/event-form-modal-component/event-form-modal.component';
import { RecurrenceDto } from '@interfaces/recurrence';
import { CreateAnnouncementPayload } from 'src/app/core/interfaces/api';
import {
  RecurrenceEditPayload,
  RecurrenceEditScope,
} from 'src/app/core/interfaces/recurrence-edit';

export interface EventEditDialogValues {
  message?: string;
  title?: string;
  noText?: string;
  yesText?: string;
  onError?: Function;
  onSuccess?: Function;
  existingActivity: Announcement;
  updatedActivity: any;
  startDateUpdated?: Boolean;
  timingUpdated?: Boolean;
  saveBatch: Function;
  form: EventFormValues;
  updatedRecurrence: RecurrenceDto;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-edit-recurrence-dialog',
  templateUrl: './edit-recurrence-dialog.component.html',
  styleUrls: ['./edit-recurrence-dialog.component.scss'],
})
export class EditRecurrenceDialogComponent implements OnInit {
  public isLoading = new BehaviorSubject<boolean>(false);
  public eventEditForm: UntypedFormGroup;
  public updatingRecurrence = false;
  public noRecurrence = false;

  constructor(
    public dialogRef: MatDialogRef<EditRecurrenceDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    private recurrenceService: RecurrenceService,

    @Inject(MAT_DIALOG_DATA) public data: EventEditDialogValues,
  ) {
    
    if (!data.updatedRecurrence) {
      this.noRecurrence = true;
    }
    
    // For "all events" updates, we should compare against the original recurrence pattern
    // regardless of which specific event date was clicked
    const functionalRecurrenceComparison = {
      freq: data.updatedRecurrence?.freq,
      interval: data.updatedRecurrence?.interval,
      // Normalize byweekday: treat null and empty array as equivalent
      byweekday: data.updatedRecurrence?.byweekday?.length > 0 ? data.updatedRecurrence.byweekday : null,
      nbyweekday: data.updatedRecurrence?.nbyweekday,
      count: data.updatedRecurrence?.count,
      until: data.updatedRecurrence?.until,
      // Always use original dtstart for comparison unless user explicitly changed start date
      dtstart: data.startDateUpdated ? data.updatedRecurrence?.dtstart : data.existingActivity.recurrence?.dtstart
    };
    
    // Normalize the existing recurrence for comparison (handle empty arrays vs null)
    const normalizedExistingRecurrence = {
      ...data.existingActivity.recurrence,
      byweekday: data.existingActivity.recurrence.byweekday?.length > 0 ? data.existingActivity.recurrence.byweekday : null,
    };
    
    const recurrenceEqualityTest = this.recurrenceService.testForRecurrenceEquality(
      normalizedExistingRecurrence,
      functionalRecurrenceComparison
    );
    
    // Only regenerate recurrence if timing/dates are actually changing or recurrence pattern changed
    const needsRecurrenceRegeneration = data.startDateUpdated || 
                                       data.timingUpdated ||
                                       !recurrenceEqualityTest;
    
    if (needsRecurrenceRegeneration) {
      this.updatingRecurrence = true;
    }
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public onConfirmClick = async ($event) => {
    this.isLoading.next(true);
    try {
      await this.handleSave();
    } catch (error) {
      // check for callback for error handling
      if (this.data.onError) {
        this.data.onError();
        this.isLoading.next(false);
        return;
      } else {
        console.error(error);
      }
    }

    this.dialogRef.close($event);
  };


  public async handleSave() {
    const scope = this.mapSelectionToScope(
      this.eventEditForm.controls['edit'].value,
    );

    if (!scope) {
      throw new Error('Invalid recurrence edit scope');
    }

    const recurrence = this.data.existingActivity.recurrence;
    if (!recurrence) {
      throw new Error('Announcement is missing recurrence data');
    }

    const timezone = this.data.existingActivity.site?.timezone;
    if (!timezone) {
      throw new Error('Announcement is missing site timezone');
    }

    const payload: RecurrenceEditPayload = {
      scope,
      announcementId: this.data.existingActivity.id,
      recurrenceId: recurrence.id,
      effectiveDate: this.getEffectiveDateIso(),
      timezone,
      updates: this.buildAnnouncementUpdates(scope),
    };

    const recurrencePatch = this.buildRecurrencePatch(scope);

    if (recurrencePatch) {
      payload.recurrencePatch = recurrencePatch;
    }

    const timings = this.buildTimingOverrides();
    if (timings) {
      payload.timings = timings;
    }

    try {
      await this.recurrenceService.editRecurringAnnouncementSeries(payload);
    } finally {
      this.isLoading.next(false);
    }

    if (this.data.onSuccess) {
      this.data.onSuccess();
    }
  }

  private mapSelectionToScope(value: string): RecurrenceEditScope | null {
    switch ((value || '').toLowerCase()) {
      case 'single':
        return 'SINGLE';
      case 'future':
        return 'THIS_AND_FUTURE';
      case 'all':
        return 'ALL';
      default:
        return null;
    }
  }

  private buildAnnouncementUpdates(
    scope: RecurrenceEditScope,
  ): Partial<CreateAnnouncementPayload> {
    const updates: Partial<CreateAnnouncementPayload> = {
      ...(this.data.updatedActivity as Partial<CreateAnnouncementPayload>),
    };

    if (scope === 'SINGLE') {
      updates.recurrenceId = null;
    } else if (this.data.existingActivity.recurrence?.id) {
      updates.recurrenceId = this.data.existingActivity.recurrence.id;
    }

    if (Array.isArray((updates as any).secondaryImages)) {
      updates.secondaryImages = (updates.secondaryImages as any).map(
        (image: any) => (typeof image === 'number' ? image : image?.id),
      );
    }

    if (Array.isArray((updates as any).eventTags)) {
      updates.eventTags = (updates.eventTags as any).map((tag: any) =>
        typeof tag === 'number' ? tag : tag?.id,
      );
    }

    return updates;
  }

  private buildRecurrencePatch(
    scope: RecurrenceEditScope,
  ): Partial<RecurrenceDto> | undefined {
    if (scope === 'SINGLE' || !this.data.updatedRecurrence) {
      return undefined;
    }

    const recurrence = this.data.updatedRecurrence;
    const patch: Partial<RecurrenceDto> = {};
    const timezone = this.data.existingActivity.site.timezone;

    if (scope === 'ALL') {
      const dtstart = this.data.startDateUpdated
        ? this.recurrenceService.getDtstartFromFormValues(
            this.data.form.timing,
            timezone,
          )
        : this.normalizeRecurrenceDate(recurrence.dtstart, timezone);
      if (dtstart) {
        patch.dtstart = dtstart;
      }
    } else {
      const dtstart = this.normalizeRecurrenceDate(
        recurrence.dtstart,
        timezone,
      );
      if (dtstart) {
        patch.dtstart = dtstart;
      }
    }

    if (recurrence.freq !== undefined) {
      patch.freq = recurrence.freq;
    }
    if (recurrence.interval !== undefined) {
      patch.interval = recurrence.interval;
    }
    if (recurrence.nbyweekday !== undefined) {
      patch.nbyweekday =
        recurrence.nbyweekday === null ? null : recurrence.nbyweekday;
    }
    if (recurrence.byweekday !== undefined) {
      patch.byweekday = recurrence.byweekday
        ? recurrence.byweekday.map((day: any) =>
            typeof day === 'number' ? day.toString() : day,
          )
        : [];
    }
    if (recurrence.count !== undefined) {
      patch.count = recurrence.count ?? null;
    }
    if (recurrence.until !== undefined) {
      patch.until = this.normalizePlainDate(recurrence.until, timezone);
    }

    // ALWAYS preserve existing exdates for ALL scope to keep deleted instances excluded
    if (scope === 'ALL') {
      if (recurrence.exdates !== undefined) {
        // Use the exdates from the updated recurrence if explicitly provided
        patch.exdates = recurrence.exdates;
      } else {
        // Always preserve existing exdates from the original recurrence
        patch.exdates = this.data.existingActivity.recurrence?.exdates || [];
      }
    }

    Object.keys(patch).forEach((key) => {
      if (patch[key as keyof RecurrenceDto] === undefined) {
        delete patch[key as keyof RecurrenceDto];
      }
    });

    return Object.keys(patch).length ? patch : undefined;
  }

  private buildTimingOverrides():
    | RecurrenceEditPayload['timings']
    | undefined {
    const timing = this.data.form?.timing;
    if (!timing) {
      return undefined;
    }
    const overrides: RecurrenceEditPayload['timings'] = {};
    const timezone = this.data.existingActivity.site?.timezone;
    const startTime = this.normalizeTimeValue(timing.eventStart, timezone);
    if (startTime) {
      overrides.startTime = startTime;
    }
    const endTime = this.normalizeTimeValue(timing.eventEnd, timezone);
    if (endTime) {
      overrides.endTime = endTime;
    }
    return Object.keys(overrides).length ? overrides : undefined;
  }

  private getEffectiveDateIso(): string {
    const value = this.data.existingActivity.eventStart;
    if (!value) {
      throw new Error('Announcement is missing event start');
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    return new Date(value).toISOString();
  }

  private normalizeRecurrenceDate(
    value: string | Date | null | undefined,
    timezone: string,
  ): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }
    if (typeof value === 'string') {
      return value;
    }
    const timing = this.data.form?.timing;
    if (!timing) {
      return value.toISOString().slice(0, 10);
    }
    return this.recurrenceService.getDtstartFromFormValues(
      { ...timing, date: value },
      timezone,
    );
  }

  private normalizePlainDate(
    value: string | Date | null | undefined,
    timezone: string,
  ): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'string') {
      return value;
    }
    const timing = this.data.form?.timing;
    if (!timing) {
      return value.toISOString().slice(0, 10);
    }
    return this.recurrenceService.getDtstartFromFormValues(
      { ...timing, date: value },
      timezone,
    );
  }

  private normalizeTimeValue(
    value: string | Date | null | undefined,
    timezone?: string,
  ): string | undefined {
    if (!value) {
      return undefined;
    }
    if (typeof value === 'string') {
      return value;
    }
    if (timezone) {
      return formatInTimeZone(value, timezone, 'HH:mm');
    }
    return format(value, 'HH:mm');
  }

  ngOnInit() {
    this.eventEditForm = this.formBuilder.group({
      edit: ['single', Validators.required],
    });
  }
}
